/**
 * This file originated from acc-rfis/src/utils/date-helpers.js
 */
import { displayDate } from '@adsk/alloy-react-datetime';
import dayjs from 'dayjs';
import add from 'date-fns/add';

export const DEFAULT_PROJECT_TIMEZONE = 'UTC';

// These timezones have no daylight savings so they are predictable for tests
export const TIMEZONE_UTC_MINUS_10 = 'Pacific/Honolulu';
export const TIMEZONE_UTC_PLUS_13 = 'Pacific/Tongatapu';

export const changeTimeZone = (date: Date | string, timeZone = 'UTC'): Date => {
  const dateToConvert = typeof date === 'string' ? new Date(date) : date;
  return new Date(dateToConvert.toLocaleString('en-US', { timeZone }));
};

export const toLongDate = (date: Date | string, timeZone = 'UTC'): string => {
  const dateToDisplay = typeof date === 'string' ? new Date(date) : date;
  return displayDate(dateToDisplay, { timeZone });
};

export const toLocalLongDate = (date: Date | string): string => {
  const dateToDisplay = typeof date === 'string' ? new Date(date) : date;
  return displayDate(dateToDisplay);
};

// Check if a dueDate is late (less than 1 day ahead)
export const isLate = (dueDate?: string | null | Date): boolean => {
  if (!dueDate) {
    return false;
  }

  return dayjs().diff(dayjs(dueDate), 'day') > 0;
};

const toUnicodeTokens = (str: string) => str.replaceAll('D', 'd').replaceAll('Y', 'y');

export const getDateFormat = () => toUnicodeTokens(dayjs.localeData().longDateFormat('L'));

export const getMidnightInTimezoneIsoString = (
  date?: Date | null | string,
  timeZone = 'UTC',
): string | null | undefined => {
  if (!date) {
    return date;
  }

  return dayjs(date)
    .hour(0)
    .minute(0)
    .second(0) // set time to 00:00:00
    .tz(timeZone, true) // convert to timezone - change the time zone without changing the current time
    .toISOString(); // e.g 2021-05-05T10:00:00.000Z
};

// Same conversion as getMidnightInTimezoneIsoString, but returns a Date instead of an ISO string.
// Used to feed a calendar-day value (whose local getters represent the intended day) into
// timezone-aware date pickers that need a real Date anchored to `timeZone`.
export const getMidnightInTimezoneDate = (
  date?: Date | null | string,
  timeZone = 'UTC',
): Date | null => {
  if (!date) {
    return null;
  }

  return dayjs(date).hour(0).minute(0).second(0).tz(timeZone, true).toDate();
};

export const removeMillisecondsFromIsoString = (dateIsoString: string): string =>
  dateIsoString.replace(/\.\d{3}Z$/, 'Z');

export const MIN_VALUE_DATE = '1970-01-01';
export const MAX_VALUE_DATE = '9999-01-01';

const getMidnightInTimezoneIsoStringWithoutMilliseconds = (
  date?: Date | null | string,
  timeZone = 'UTC',
): string | null | undefined => {
  const dateInIsoString = getMidnightInTimezoneIsoString(date, timeZone);
  return dateInIsoString && removeMillisecondsFromIsoString(dateInIsoString);
};

const getEndOfDayInTimezoneIsoStringWithoutMilliseconds = (
  date?: Date | null | string,
  timeZone = 'UTC',
): string | null => {
  if (!date) {
    return null;
  }

  const dateInIsoString = dayjs(date)
    .hour(23)
    .minute(59)
    .second(59) // set time to 00:00:00
    .tz(timeZone, true) // convert to timezone - change the time zone without changing the current time
    .toISOString(); // e.g 2021-05-05T10:00:00.000Z

  return removeMillisecondsFromIsoString(dateInIsoString);
};

export type FormatFilterDateRangeArgs = {
  startDate?: string | null; // In YYYY-MM-DD format
  endDate?: string | null; // In YYYY-MM-DD format
  timeZone?: string;
};

/** Return value example - "2022-08-03T00:00:00Z,2022-08-09T23:59:59Z"  */
export const formatFilterDateRange = ({
  startDate,
  endDate,
  timeZone = 'UTC',
}: FormatFilterDateRangeArgs): string => {
  const start = startDate || MIN_VALUE_DATE;
  const end = endDate || MAX_VALUE_DATE;
  const formattedStartDate = getMidnightInTimezoneIsoStringWithoutMilliseconds(start, timeZone);
  const formattedEndDate = getEndOfDayInTimezoneIsoStringWithoutMilliseconds(end, timeZone);
  return `${formattedStartDate},${formattedEndDate}`;
};

/**
 * Returns a new Date object that is incremented by the specified number of days from today.
 * @param days - The number of days to increment today's date by.
 * @returns A new Date object that is incremented by the specified number of days from today.
 */
export const incrementTodayByDays = (days: number): Date => {
  const today = new Date();
  const todayInDatePicker = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 0, 0);
  return add(todayInDatePicker, { days });
};
